// AdminDashboard.js (Final with updated RA Bill query using date string)
import React, { useEffect, useState } from 'react';
import { db, auth } from '../firebase';
import {
  getDocs,
  collection,
  doc,
  getDoc,
  onSnapshot,
  updateDoc,
  query,
  where,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import '../style/common.css';
import './AdminDashboard.css';
import AdminSidebar from '../components/AdminSidebar';

function AccordionSection({ title, children }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="accordion-section">
      <div className="accordion-header inline" onClick={() => setOpen(!open)}>
        <h2 className="accordion-title">{title}</h2>
        <button className="accordion-toggle-btn">{open ? '-' : '+'}</button>
      </div>
      {open && <div className="accordion-content">{children}</div>}
    </div>
  );
}

function AdminDashboard() {
  const [pos, setPOs] = useState([]);
  const [users, setUsers] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [raTotalSinceApril, setRATotalSinceApril] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    let unsubscribeUsers = () => {};
    let unsubscribeBills = () => {};

    const fetchData = async (user) => {
      try {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) setCurrentUser(userSnap.data());

        unsubscribeUsers = onSnapshot(collection(db, 'users'), (snapshot) => {
          const userMap = {};
          snapshot.forEach((doc) => {
            const data = doc.data();
            userMap[doc.id] = data.name || data.email || 'Unnamed';
          });
          setUsers(userMap);
        });

        const currentYear = new Date().getFullYear();
        const startDateStr = `${currentYear}-04-01`;

        const raQuery = query(
          collection(db, 'ra_bills'),
          where('bill_date', '>=', startDateStr)
        );

        const raSnapshot = await getDocs(raQuery);
        let raTotal = 0;
        const billMap = {};

        raSnapshot.docs.forEach((bill) => {
          const data = bill.data();
          raTotal += parseFloat(data.amount || 0);
          billMap[data.po_id] = (billMap[data.po_id] || 0) + parseFloat(data.amount || 0);
        });

        setRATotalSinceApril(raTotal);

        unsubscribeBills = onSnapshot(collection(db, 'ra_bills'), async (billSnap) => {
          const billMapLive = {};
          billSnap.docs.forEach((bill) => {
            const data = bill.data();
            billMapLive[data.po_id] =
              (billMapLive[data.po_id] || 0) + parseFloat(data.amount || 0);
          });

          const poSnap = await getDocs(collection(db, 'purchase_orders'));
          const allPOs = [];

          for (const poDoc of poSnap.docs) {
            const po = { id: poDoc.id, ...poDoc.data() };
            const total = parseFloat(po.po_total_amount || 0);
            const billed = parseFloat(billMapLive[po.id] || 0);
            const balance = parseFloat((total - billed).toFixed(2));
            const alreadyCompleted = po.completed === true;
            const shouldAutoComplete = !alreadyCompleted && Math.abs(balance) < 0.01;
            const needsUpdate = po.balance_amount !== balance || shouldAutoComplete;

            if (needsUpdate) {
              const updatePayload = {
                ...(po.balance_amount !== balance && { balance_amount: balance }),
                ...(shouldAutoComplete && { completed: true }),
              };
              await updateDoc(doc(db, 'purchase_orders', poDoc.id), updatePayload);
            }

            po.balance_amount = balance;
            po.completed = alreadyCompleted || shouldAutoComplete;
            allPOs.push(po);
          }

          setPOs(allPOs);
        });
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      }
    };

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchData(user);
      } else {
        navigate('/login');
      }
    });

    return () => {
      unsubscribeUsers();
      unsubscribeBills();
      unsubscribeAuth();
    };
  }, [navigate]);

  const navigateTo = (path) => navigate(path);

  const daysBetween = (start, end = new Date()) => {
    const s = new Date(start);
    const e = new Date(end);
    return Math.floor((e - s) / (1000 * 60 * 60 * 24));
  };

  const totalAmount = pos.reduce((sum, po) => sum + (po.po_total_amount || 0), 0);
  const activePOs = pos.filter((po) => !po.completed);
  const closedPOs = pos.filter((po) => po.completed);
  const totalReceivables = activePOs.reduce(
    (sum, po) => sum + (po.balance_amount || 0),
    0
  );

  const amountByPM = {};
  const totalByPM = {};
  activePOs.forEach((po) => {
    if (po.assigned_to) {
      amountByPM[po.assigned_to] =
        (amountByPM[po.assigned_to] || 0) + (po.balance_amount || 0);
      totalByPM[po.assigned_to] =
        (totalByPM[po.assigned_to] || 0) + (po.po_total_amount || 0);
    }
  });

  const delayedPOs = activePOs.filter(
    (po) => po.deadline && new Date(po.deadline) < new Date()
  );

  const warningPOs = activePOs.filter((po) => {
    if (!po.deadline) return false;
    const daysLeft = daysBetween(new Date(), po.deadline);
    return daysLeft >= 0 && daysLeft <= 10;
  });

  return (
    <div className="dashboard-wrapper">
      <AdminSidebar user={currentUser} />
      <div className="dashboard-content">
        <h2 className="dashboard-title">Admin Dashboard</h2>

        <div className="dashboard-stats">
          <div className="stat-card clickable" onClick={() => navigateTo('/admin/po-list?filter=all')}>
            <h3>Total POs</h3>
            <p>{pos.length}</p>
          </div>
          <div className="stat-card clickable" onClick={() => navigateTo('/admin/po-list?filter=active')}>
            <h3>Running POs</h3>
            <p>{activePOs.length}</p>
          </div>
          <div className="stat-card clickable" onClick={() => navigateTo('/admin/po-list?filter=completed')}>
            <h3>Closed POs</h3>
            <p>{closedPOs.length}</p>
          </div>
          <div className="stat-card">
            <h3>Total PO Amount</h3>
            <p>₹{totalAmount.toLocaleString('en-IN')}</p>
          </div>
          <div className="stat-card">
            <h3>Total PO Pending Amount</h3>
            <p>₹{totalReceivables.toLocaleString('en-IN')}</p>
          </div>
          <div className="stat-card clickable" onClick={() => navigateTo('/admin/ra-after-april')}>
            <h3>RA Bills (Since April)</h3>
            <p>₹{raTotalSinceApril.toLocaleString('en-IN')}</p>
          </div>
          <div className="stat-card clickable" onClick={() => navigateTo('/admin/client-wise')}>
            <h3>Company-wise PO Details</h3>
          </div>
          <div className="stat-card clickable" onClick={() => navigateTo('/admin/vendor-details')}>
            <h3>Vendor Details</h3>
          </div>
          <div className="stat-card clickable" onClick={() => navigateTo('/admin/materials')}>
            <h3>Material Lookup</h3>
          </div>
        </div>

        <AccordionSection title="Pending Amount by Project Managers">
          <div className="dashboard-stats">
            {Object.keys(amountByPM).map((uid) => (
              <div className="stat-card" key={uid}>
                <h3 className="clickable manager-name" onClick={() => navigateTo(`/admin/view-progress/${uid}`)}>
                  {users[uid] || uid}
                </h3>
                <p>₹{amountByPM[uid].toLocaleString('en-IN')}</p>
                <small>Total: ₹{totalByPM[uid]?.toLocaleString('en-IN')}</small>
              </div>
            ))}
          </div>
        </AccordionSection>

        <AccordionSection title="Delayed POs">
          <div className="po-list-2">
            {delayedPOs.length === 0 ? (
              <p>No delayed POs found.</p>
            ) : (
              delayedPOs.map((po) => (
                <div
                  className="po-card clickable"
                  key={po.id}
                  onClick={() => navigateTo(`/admin/po-detail/${po.id}`)}
                >
                  <p><strong>JC Number:</strong> {po.jc_number}</p>
                  <p><strong>Location:</strong> {po.location}</p>
                  <p><strong>Total Amount:</strong> ₹{po.po_total_amount?.toLocaleString('en-IN')}</p>
                  <p><strong>Start Date:</strong> {po.po_date}</p>
                  <p><strong>Days Overdue:</strong> {daysBetween(po.deadline)} days</p>
                  <p><strong>Assigned To:</strong> {users[po.assigned_to] || 'Not assigned'}</p>
                </div>
              ))
            )}
          </div>
        </AccordionSection>

        <AccordionSection title="POs Near Deadline">
          <div className="po-list-2">
            {warningPOs.length === 0 ? (
              <p>No POs delaying in 10 days.</p>
            ) : (
              warningPOs.map((po) => (
                <div
                  className="po-card warning clickable"
                  key={po.id}
                  onClick={() => navigateTo(`/admin/po-detail/${po.id}`)}
                >
                  <p><strong>PO Number:</strong> {po.po_number}</p>
                  <p><strong>JC Number:</strong> {po.jc_number}</p>
                  <p><strong>Description:</strong> {po.description}</p>
                  <p><strong>Location:</strong> {po.location}</p>
                  <p><strong>Total Amount:</strong> ₹{po.po_total_amount?.toLocaleString('en-IN')}</p>
                  <p><strong>Balance:</strong> ₹{po.balance_amount?.toLocaleString('en-IN')}</p>
                  <p><strong>Deadline:</strong> {po.deadline}</p>
                  <p><strong>Days to Deadline:</strong> {daysBetween(new Date(), po.deadline)} days</p>
                  <p><strong>Assigned To:</strong> {users[po.assigned_to] || 'Not assigned'}</p>
                </div>
              ))
            )}
          </div>
        </AccordionSection>
      </div>
    </div>
  );
}

export default AdminDashboard;